import {
	IconChevronLeft,
	IconChevronRight,
	IconPlus,
	IconRefresh,
	IconTrash,
} from "@tabler/icons-react";
import { Button, Flex, Form, Modal, Segmented, Space, Typography } from "antd";
import { useMemo, useState } from "react";
import {
	useAddWorkspaceMember,
	useOrgUsers,
	useRemoveWorkspaceMember,
	useUpdateWorkspaceMemberRole,
	useWorkspaceMembersPaged,
} from "@/common/artemis-operations/members";
import { confirmDestructive } from "@/common/confirm";
import {
	DELETE_RESOURCE_ERROR_FAILED,
	DELETE_RESOURCE_INFO_COMPLETED,
	UPDATE_RESOURCE_ERROR_FAILED,
	UPDATE_RESOURCE_INFO_COMPLETED,
} from "@/common/messages";
import type { OrgUser, WorkspaceMember } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { BaseContainer, CustomIcon, QueryGuard } from "@/components/base";
import { CustomList } from "@/components/custom-list";
import { UserSearchSelect } from "@/components/users/user-search-select";

function AddMemberModal({
	workspaceId,
	open,
	onClose,
	existingUserIds,
}: {
	workspaceId: string;
	open: boolean;
	onClose: () => void;
	existingUserIds: Set<string>;
}) {
	const [form] = Form.useForm();
	const notify = useNotify();
	const addMember = useAddWorkspaceMember(workspaceId);

	function handleClose() {
		form.resetFields();
		onClose();
	}

	function handleOk() {
		form.validateFields().then((values) => {
			addMember.mutate(
				{
					workspaceId,
					userId: values.userId,
					role: values.role ?? "PARTICIPANT",
				},
				{
					onSuccess: () => {
						notify.success("Member added.");
						handleClose();
					},
					onError: (err) =>
						notify.error((err as Error).message || "Failed to add member."),
				},
			);
		});
	}

	return (
		<Modal
			title="Add member"
			open={open}
			onCancel={handleClose}
			onOk={handleOk}
			okText="Add"
			confirmLoading={addMember.isPending}
			destroyOnHidden
		>
			<Form form={form} layout="vertical" style={{ marginTop: 16 }}>
				<Form.Item
					name="userId"
					label="User"
					rules={[{ required: true, message: "Select a user" }]}
				>
					<UserSearchSelect excludeIds={existingUserIds} />
				</Form.Item>
				<Form.Item name="role" label="Role" initialValue="PARTICIPANT">
					<Segmented
						options={[
							{ value: "PARTICIPANT", label: "Participant" },
							{ value: "WORKSPACE_ADMIN", label: "Admin" },
						]}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
}

function MemberItem({
	member,
	user,
	workspaceId,
	isLastAdmin,
}: {
	member: WorkspaceMember;
	user: OrgUser | undefined;
	workspaceId: string;
	isLastAdmin: boolean;
}) {
	const notify = useNotify();
	const removeMember = useRemoveWorkspaceMember(workspaceId);
	const updateRole = useUpdateWorkspaceMemberRole(workspaceId);

	function handleRemove() {
		confirmDestructive({
			title: "Remove member",
			content: `Remove ${user?.displayName ?? user?.email ?? member.userId} from this workspace?`,
			onOk() {
				removeMember.mutate(
					{ userId: member.userId },
					{
						onSuccess: () => notify.success(DELETE_RESOURCE_INFO_COMPLETED),
						onError: (err) =>
							notify.error(
								(err as Error).message || DELETE_RESOURCE_ERROR_FAILED,
							),
					},
				);
			},
		});
	}

	function handleRoleChange(role: string) {
		updateRole.mutate(
			{
				workspaceId,
				userId: member.userId,
				role: role as "PARTICIPANT" | "WORKSPACE_ADMIN",
			},
			{
				onSuccess: () => notify.success(UPDATE_RESOURCE_INFO_COMPLETED),
				onError: (err) =>
					notify.error((err as Error).message || UPDATE_RESOURCE_ERROR_FAILED),
			},
		);
	}

	return (
		<Flex
			justify="space-between"
			align="center"
			gap={8}
			wrap
			style={{ width: "100%", height: "100%" }}
		>
			<Flex
				vertical
				style={{ minWidth: 0, flex: "1 1 200px", overflow: "hidden" }}
			>
				<Typography.Text
					strong
					ellipsis={{
						tooltip: user?.displayName ?? user?.email ?? member.userId,
					}}
				>
					{user?.displayName || user?.email || member.userId}
				</Typography.Text>
				{user?.email && (
					<Typography.Text type="secondary" ellipsis={{ tooltip: user.email }}>
						{user.email}
					</Typography.Text>
				)}
			</Flex>
			<Space style={{ flexShrink: 0 }}>
				<Segmented
					value={member.role}
					disabled={
						updateRole.isPending ||
						(isLastAdmin && member.role === "WORKSPACE_ADMIN")
					}
					onChange={(v) => handleRoleChange(v as string)}
					options={[
						{ value: "PARTICIPANT", label: "Participant" },
						{ value: "WORKSPACE_ADMIN", label: "Admin" },
					]}
				/>
				<Button
					icon={<CustomIcon icon={IconTrash} />}
					type="text"
					danger
					onClick={handleRemove}
					loading={removeMember.isPending}
					disabled={isLastAdmin && member.role === "WORKSPACE_ADMIN"}
				/>
			</Space>
		</Flex>
	);
}

/** Displays and manages workspace membership: list, add, remove, and role changes. */
export function WorkspaceMembers({ workspaceId }: { workspaceId: string }) {
	const [addOpen, setAddOpen] = useState(false);
	const members = useWorkspaceMembersPaged(workspaceId);
	const orgUsers = useOrgUsers();

	const userMap = useMemo(() => {
		const map = new Map<string, OrgUser>();
		for (const u of orgUsers.data ?? []) map.set(u.id, u);
		return map;
	}, [orgUsers.data]);

	// ponytail: dedup/last-admin guard use the current page; the server enforces both authoritatively.
	const existingUserIds = useMemo(
		() => new Set(members.rows.map((m) => m.userId)),
		[members.rows],
	);

	const adminCount = useMemo(
		() => members.rows.filter((m) => m.role === "WORKSPACE_ADMIN").length,
		[members.rows],
	);

	return (
		<BaseContainer
			title="Members"
			actions={
				<Space>
					<Button
						icon={<CustomIcon icon={IconRefresh} />}
						type="text"
						loading={members.isFetching}
						onClick={() => members.refetch()}
					/>
					<Button
						icon={<CustomIcon icon={IconPlus} />}
						type="text"
						onClick={() => setAddOpen(true)}
					>
						Add
					</Button>
				</Space>
			}
		>
			<QueryGuard
				isPending={members.loading || orgUsers.isPending}
				isError={members.isError || orgUsers.isError}
				isNull={false}
			>
				<CustomList<WorkspaceMember>
					data={members.rows}
					renderItem={(member) => (
						<MemberItem
							member={member}
							user={userMap.get(member.userId)}
							workspaceId={workspaceId}
							isLastAdmin={adminCount <= 1}
						/>
					)}
					onAdd={() => setAddOpen(true)}
				/>
				<Flex justify="flex-end" align="center" gap={8}>
					<Typography.Text type="secondary" style={{ fontSize: "0.8rem" }}>
						Page {members.pageIndex + 1} of{" "}
						{Math.max(1, Math.ceil(members.totalCount / members.pageSize))} ·{" "}
						{members.totalCount} items
					</Typography.Text>
					<Button
						size="small"
						type="text"
						disabled={!members.hasPreviousPage}
						onClick={members.onPreviousPage}
						icon={<CustomIcon icon={IconChevronLeft} />}
					>
						Previous
					</Button>
					<Button
						size="small"
						type="text"
						disabled={!members.hasNextPage}
						onClick={members.onNextPage}
						iconPlacement="end"
						icon={<CustomIcon icon={IconChevronRight} />}
					>
						Next
					</Button>
				</Flex>
			</QueryGuard>

			<AddMemberModal
				workspaceId={workspaceId}
				open={addOpen}
				onClose={() => setAddOpen(false)}
				existingUserIds={existingUserIds}
			/>
		</BaseContainer>
	);
}
